import { Link } from "@tanstack/react-router";
import { BarChart3, Check, ExternalLink, Search, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FY_OPTIONS, getCurrentFiscalQuarter } from "@/components/deal-metrics/shared";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatFY } from "@/lib/fiscal";
import { cn } from "@/lib/utils";

interface SectionCounts {
  metricGoals: number;
  nonRelationalGoals: number;
  affiliateGoals: number;
  campusTargets: number;
  createdGoals: number;
  fpReportingGoals: number;
  sourceGoalSets: number;
  campusAffiliates: number;
  campusBudget: boolean;
  affiliateMappings: number;
}

interface OwnerOverview {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string | null;
  managerId: string | null;
  managerName: string | null;
  commissionType: string | null;
  executiveView: boolean;
  commissionFieldsSet: number;
  commissionFieldsTotal: number;
  sections: SectionCounts;
}

type SectionKey = keyof SectionCounts;

const SECTION_COLUMNS: Array<{
  key: SectionKey;
  label: string;
  fullLabel: string;
  group: "goals" | "campus" | "coaching";
}> = [
  { key: "metricGoals", label: "Metric", fullLabel: "Metric goals", group: "goals" },
  {
    key: "nonRelationalGoals",
    label: "NR",
    fullLabel: "Non-relational goals",
    group: "goals",
  },
  { key: "affiliateGoals", label: "Affiliate", fullLabel: "Affiliate goals", group: "goals" },
  { key: "sourceGoalSets", label: "Source", fullLabel: "Source goal sets", group: "goals" },
  { key: "createdGoals", label: "Created", fullLabel: "Created goals", group: "goals" },
  { key: "fpReportingGoals", label: "FP", fullLabel: "FP reporting goals", group: "goals" },
  {
    key: "campusTargets",
    label: "Targets",
    fullLabel: "Campus KPI targets",
    group: "campus",
  },
  {
    key: "campusAffiliates",
    label: "Affiliates",
    fullLabel: "Assigned campuses",
    group: "campus",
  },
  { key: "campusBudget", label: "Budget", fullLabel: "Campus budget", group: "campus" },
  {
    key: "affiliateMappings",
    label: "Aff mapping",
    fullLabel: "Affiliate coaching mappings",
    group: "coaching",
  },
];

const GROUP_LABEL: Record<"goals" | "campus" | "coaching", string> = {
  goals: "Goals",
  campus: "Campus",
  coaching: "Coaching",
};

function hasAnyConfiguration(o: OwnerOverview): boolean {
  if (o.commissionFieldsSet > 0) return true;
  const s = o.sections;
  return (
    s.metricGoals > 0 ||
    s.nonRelationalGoals > 0 ||
    s.affiliateGoals > 0 ||
    s.campusTargets > 0 ||
    s.createdGoals > 0 ||
    s.fpReportingGoals > 0 ||
    s.sourceGoalSets > 0 ||
    s.campusAffiliates > 0 ||
    s.campusBudget ||
    s.affiliateMappings > 0
  );
}

function ConfiguredCell({ value, label }: { value: number | boolean; label: string }) {
  const populated = typeof value === "boolean" ? value : value > 0;
  if (!populated) {
    return <span className="text-muted-foreground/40">—</span>;
  }
  const showCount = typeof value === "number" && value > 1;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center justify-center gap-1 text-emerald-600">
          <Check className="h-4 w-4" />
          {showCount && <span className="text-xs font-medium tabular-nums">{value}</span>}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {label}
        {typeof value === "number" && `: ${value}`}
      </TooltipContent>
    </Tooltip>
  );
}

function CommissionCell({ owner }: { owner: OwnerOverview }) {
  const { commissionFieldsSet, commissionFieldsTotal, commissionType } = owner;
  const populated = commissionFieldsSet > 0;
  const typeLabel =
    commissionType === "non_relational"
      ? "Non-relational"
      : commissionType === "standard_margin"
        ? "Standard margin"
        : "Standard";
  return (
    <div className="flex flex-col gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1 text-xs font-medium tabular-nums",
              populated ? "text-foreground" : "text-muted-foreground/60",
            )}
          >
            <span
              className={cn(
                "inline-block h-1.5 w-1.5 rounded-full",
                populated ? "bg-emerald-500" : "bg-muted-foreground/30",
              )}
            />
            {commissionFieldsSet}/{commissionFieldsTotal}
          </span>
        </TooltipTrigger>
        <TooltipContent>{commissionFieldsSet} commission fields set</TooltipContent>
      </Tooltip>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {typeLabel}
      </span>
    </div>
  );
}

export function OwnersOverviewPage() {
  const { fy: currentFy } = getCurrentFiscalQuarter();
  const [selectedFy, setSelectedFy] = useState(currentFy);
  const [owners, setOwners] = useState<OwnerOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [hideUnconfigured, setHideUnconfigured] = useState(true);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/overview?fy=${selectedFy}`);
      if (res.ok) {
        const data = await res.json();
        setOwners(data.owners ?? []);
      } else {
        setOwners([]);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedFy]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return owners.filter((o) => {
      if (hideUnconfigured && !hasAnyConfiguration(o)) return false;
      if (!q) return true;
      const name = `${o.firstName} ${o.lastName}`.toLowerCase();
      return (
        name.includes(q) ||
        o.email.toLowerCase().includes(q) ||
        (o.role ?? "").toLowerCase().includes(q) ||
        (o.managerName ?? "").toLowerCase().includes(q)
      );
    });
  }, [owners, search, hideUnconfigured]);

  const hiddenCount = owners.length - filtered.length;

  const summary = useMemo(() => {
    const total = owners.length;
    const withCommission = owners.filter((o) => o.commissionFieldsSet > 0).length;
    const withAnyGoal = owners.filter((o) => {
      const s = o.sections;
      return (
        s.metricGoals > 0 ||
        s.nonRelationalGoals > 0 ||
        s.affiliateGoals > 0 ||
        s.campusTargets > 0 ||
        s.createdGoals > 0 ||
        s.fpReportingGoals > 0 ||
        s.sourceGoalSets > 0
      );
    }).length;
    const withCampus = owners.filter((o) => {
      const s = o.sections;
      return s.campusTargets > 0 || s.campusAffiliates > 0 || s.campusBudget;
    }).length;
    const withCoaching = owners.filter((o) => o.sections.affiliateMappings > 0).length;
    return { total, withCommission, withAnyGoal, withCampus, withCoaching };
  }, [owners]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <PageHeader
            title="Owners Overview"
            description="Bird's-eye view of commission and goal configuration across every owner"
          />
          <Select value={String(selectedFy)} onValueChange={(v) => setSelectedFy(Number(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FY_OPTIONS.map((fy) => (
                <SelectItem key={fy} value={String(fy)}>
                  {formatFY(fy)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <SummaryStat label="Owners" value={summary.total} />
          <SummaryStat
            label="With commission"
            value={summary.withCommission}
            denom={summary.total}
          />
          <SummaryStat label="With any goal" value={summary.withAnyGoal} denom={summary.total} />
          <SummaryStat label="With campus" value={summary.withCampus} denom={summary.total} />
          <SummaryStat label="With coaching" value={summary.withCoaching} denom={summary.total} />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by name, email, role, or manager…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3">
            {hideUnconfigured && hiddenCount > 0 && (
              <span className="text-xs text-muted-foreground">{hiddenCount} hidden</span>
            )}
            <div className="flex items-center gap-2">
              <Switch
                id="hide-unconfigured"
                checked={hideUnconfigured}
                onCheckedChange={setHideUnconfigured}
              />
              <Label htmlFor="hide-unconfigured" className="text-sm cursor-pointer">
                Hide owners with nothing configured
              </Label>
            </div>
          </div>
        </div>

        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-muted/40">
                <TableRow>
                  <TableHead
                    rowSpan={2}
                    className="sticky left-0 z-20 min-w-[260px] bg-muted/40 align-bottom"
                  >
                    Owner
                  </TableHead>
                  <TableHead rowSpan={2} className="align-bottom">
                    Commission
                  </TableHead>
                  {(["goals", "campus", "coaching"] as const).map((group) => {
                    const cols = SECTION_COLUMNS.filter((c) => c.group === group);
                    return (
                      <TableHead
                        key={group}
                        colSpan={cols.length}
                        className="text-center text-[10px] uppercase tracking-widest text-muted-foreground"
                      >
                        {GROUP_LABEL[group]}
                      </TableHead>
                    );
                  })}
                  <TableHead rowSpan={2} className="text-right align-bottom">
                    Actions
                  </TableHead>
                </TableRow>
                <TableRow>
                  {SECTION_COLUMNS.map((col) => (
                    <TableHead key={col.key} className="text-center text-xs whitespace-nowrap">
                      {col.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={2 + SECTION_COLUMNS.length + 1}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={2 + SECTION_COLUMNS.length + 1}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      {search ? "No owners match your filter." : "No owners found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((o) => (
                    <TableRow key={o.id} className="group">
                      <TableCell className="sticky left-0 z-10 bg-background group-hover:bg-muted/40 min-w-[260px]">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {o.firstName} {o.lastName}
                            </span>
                            {o.executiveView && (
                              <Badge
                                variant="outline"
                                className="border-brand-gold/40 text-brand-gold text-[10px] px-1.5 py-0"
                              >
                                Exec
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{o.email}</span>
                          {(o.role || o.managerName) && (
                            <span className="text-xs text-muted-foreground">
                              {o.role}
                              {o.role && o.managerName && " · "}
                              {o.managerName && `Reports to ${o.managerName}`}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <CommissionCell owner={o} />
                      </TableCell>
                      {SECTION_COLUMNS.map((col) => {
                        const value = o.sections[col.key];
                        return (
                          <TableCell key={col.key} className="text-center">
                            <ConfiguredCell value={value} label={col.fullLabel} />
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button asChild size="icon" variant="ghost" className="h-7 w-7">
                                <Link
                                  to="/owner-detail/$ownerId"
                                  params={{ ownerId: o.id }}
                                  search={{ from: "owners-overview" }}
                                >
                                  <User className="h-4 w-4" />
                                </Link>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Open profile</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button asChild size="icon" variant="ghost" className="h-7 w-7">
                                <Link
                                  to="/owner-metrics/$ownerId"
                                  params={{ ownerId: o.id }}
                                  search={{ from: "owners-overview" }}
                                >
                                  <BarChart3 className="h-4 w-4" />
                                </Link>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Open dashboard</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button asChild size="icon" variant="ghost" className="h-7 w-7">
                                <Link
                                  to="/owner-detail/$ownerId"
                                  params={{ ownerId: o.id }}
                                  search={{ from: "owners-overview", tab: "goals" }}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Link>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Open goals & targets</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </TooltipProvider>
  );
}

function SummaryStat({ label, value, denom }: { label: string; value: number; denom?: number }) {
  return (
    <Card className="p-4">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-semibold font-serif tabular-nums">{value}</span>
        {denom !== undefined && (
          <span className="text-sm text-muted-foreground tabular-nums">/ {denom}</span>
        )}
      </div>
    </Card>
  );
}
